import React from "react";
import { formateDateWithMonth, formatSecondstoHHMMSS } from "../../../../utils/commonfunction";
import { Document, Page, Text, View, StyleSheet, Font } from "@react-pdf/renderer";
import { pdf } from "@react-pdf/renderer";

Font.register({
    family: "Roboto",
    src: "https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxP.ttf",
    fontWeight: "normal"
});

const SessionLogDetails = ({ modalData, handleCloseNotes }) => {
    const prepareSessionData = (row) => {
        const data = {};
        if (row.booking_tracking_id) data.bookingTrackingId = row.booking_tracking_id;
        if (row.user?.name) data.userName = row.user.name;
        if (row.astrologer?.name) data.astrologerName = row.astrologer.name;
        if (row.service_type) data.serviceType = row.service_type.replace(/_/g, " ");
        if (row.mode_of_consultation) data.mode = row.mode_of_consultation;
        if (row.duration) data.duration = `${formatSecondstoHHMMSS(row.duration)} (${row.charged_duration_in_minutes} min charged)`;
        if (row.base_price_per_minute !== null && row.base_price_per_minute !== undefined) data.basePrice = `${Number(row.base_price_per_minute).toFixed(2)}`;
        if (row.astrologer_offer_percentage !== null && row.astrologer_offer_percentage !== undefined) data.astroDiscount = `${row.astrologer_offer_percentage}%`;
        if (row.platform_offer_percentage !== null && row.platform_offer_percentage !== undefined) data.platformDiscount = `${row.platform_offer_percentage}%`;
        if (row.final_amount !== null && row.final_amount !== undefined) data.totalAmount = `${Number(row.final_amount).toFixed(2)}`;
        if (row.astrologer_earning_amount !== null && row.astrologer_earning_amount !== undefined) data.astroEarnings = `${Number(row.astrologer_earning_amount).toFixed(2)}`;
        if (row.platform_commission_percentage !== null && row.platform_commission_percentage !== undefined) data.platformCommission = `${Number(row.platform_commission_percentage).toFixed(2)}%`;
        if (row.platform_commission_amount !== null && row.platform_commission_amount !== undefined) data.platformEarnings = `${Number(row.platform_commission_amount).toFixed(2)}`;
        if (row.session_notes && row.session_notes.trim() !== "") data.sessionNotes = row.session_notes;
        if (row.createdAt) data.createdAt = formateDateWithMonth(row.createdAt);
        return data;
    };

    const styles = StyleSheet.create({
        page: { padding: 40 },
        header: { marginBottom: 30, borderBottomWidth: 3, borderBottomColor: "#403fad", paddingBottom: 15 },
        headerTitle: { fontSize: 28, color: "#403fad", textAlign: "center", marginBottom: 5 },
        headerSubtitle: { fontSize: 12, color: "#666", textAlign: "center" },
        section: { marginBottom: 20 },
        sectionTitle: { backgroundColor: "#f0f0f0", padding: 10, fontWeight: "bold", color: "#403fad", borderLeftWidth: 4, borderLeftColor: "#403fad", marginBottom: 10 },
        row: { display: "flex", flexDirection: "row", justifyContent: "space-between", paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: "#eee" },
        label: { fontWeight: "bold", color: "#403fad", width: "45%" },
        value: { color: "#333", width: "55%", textAlign: "right" },
        notes: { backgroundColor: "#f9f9f9", padding: 12, borderLeftWidth: 4, borderLeftColor: "#403fad", fontSize: 10 },
        footer: { marginTop: 40, textAlign: "center", fontSize: 11, color: "#999", borderTopWidth: 1, borderTopColor: "#eee", paddingTop: 10 },
        // currency: { fontFamily: "Roboto", color: "#333", width: "55%", textAlign: "right" },
        currency: { fontFamily: "Roboto", color: "#333", textAlign: "right" },
    });

    // Helper to check if section has any data
    const hasParticipantData = (data) => data.userName || data.astrologerName;
    const hasConsultationData = (data) => data.serviceType || data.mode || data.duration;
    const hasPricingData = (data) => data.basePrice || data.astroDiscount || data.platformDiscount || data.totalAmount;
    const hasEarningsData = (data) => data.astroEarnings || data.platformCommission || data.platformEarnings;

    const handleDownloadNotesPDF = async (row) => {
        const data = prepareSessionData(row);

        const MyDocument = (
            <Document>
                <Page size="A4" style={styles.page}>
                    <View style={styles.header}>
                        <Text style={styles.headerTitle}>Session Details Report</Text>
                        <Text style={styles.headerSubtitle}>Generated on {new Date().toLocaleString()}</Text>
                    </View>

                    {/* Participant Information - Only show if has data */}
                    {hasParticipantData(data) && (
                        <View style={styles.section}>
                            <Text style={styles.sectionTitle}>Participant Information</Text>
                            {data.userName && (
                                <View style={styles.row}>
                                    <Text style={styles.label}>User Name:</Text>
                                    <Text style={styles.value}>{data.userName}</Text>
                                </View>
                            )}
                            {data.astrologerName && (
                                <View style={styles.row}>
                                    <Text style={styles.label}>Astrologer Name:</Text>
                                    <Text style={styles.value}>{data.astrologerName}</Text>
                                </View>
                            )}
                        </View>
                    )}

                    {/* Consultation Details - Only show if has data */}
                    {hasConsultationData(data) && (
                        <View style={styles.section}>
                            <Text style={styles.sectionTitle}>Consultation Details</Text>
                            {data.bookingTrackingId && (
                                <View style={styles.row}>
                                    <Text style={styles.label}>Booking ID:</Text>
                                    <Text style={styles.value}>{`#${data.bookingTrackingId}`}</Text>
                                </View>
                            )}
                            {data.serviceType && (
                                <View style={styles.row}>
                                    <Text style={styles.label}>Service Type:</Text>
                                    <Text style={styles.value}>{data.serviceType}</Text>
                                </View>
                            )}
                            {data.mode && (
                                <View style={styles.row}>
                                    <Text style={styles.label}>Mode:</Text>
                                    <Text style={styles.value}>{data.mode}</Text>
                                </View>
                            )}
                            {data.duration && (
                                <View style={styles.row}>
                                    <Text style={styles.label}>Duration:</Text>
                                    <Text style={styles.value}>{data.duration}</Text>
                                </View>
                            )}
                        </View>
                    )}

                    {/* Pricing & Discounts - Only show if has data */}
                    {hasPricingData(data) && (
                        <View style={styles.section}>
                            <Text style={styles.sectionTitle}>Pricing & Discounts</Text>
                            {data.basePrice && (
                                <View style={styles.row}>
                                    <Text style={styles.label}>Base Price/Min:</Text>
                                    <Text style={styles.currency}>{data.basePrice}</Text>
                                </View>
                            )}
                            {data.astroDiscount && (
                                <View style={styles.row}>
                                    <Text style={styles.label}>Astrologer Discount:</Text>
                                    <Text style={styles.value}>{data.astroDiscount}</Text>
                                </View>
                            )}
                            {data.platformDiscount && (
                                <View style={styles.row}>
                                    <Text style={styles.label}>Platform Discount:</Text>
                                    <Text style={styles.value}>{data.platformDiscount}</Text>
                                </View>
                            )}
                            {data.totalAmount && (
                                <View style={styles.row}>
                                    <Text style={styles.label}>Total Amount:</Text>
                                    <Text style={{ ...styles.currency, fontWeight: "bold", color: "#27ae60" }}>{data.totalAmount}</Text>
                                </View>
                            )}
                        </View>
                    )}

                    {/* Earnings & Commission - Only show if has data */}
                    {hasEarningsData(data) && (
                        <View style={styles.section}>
                            <Text style={styles.sectionTitle}>Earnings & Commission</Text>
                            {data.astroEarnings && (
                                <View style={styles.row}>
                                    <Text style={styles.label}>Astrologer Earnings:</Text>
                                    <Text style={styles.currency}>{data.astroEarnings}</Text>
                                </View>
                            )}
                            {data.platformCommission && (
                                <View style={styles.row}>
                                    <Text style={styles.label}>Platform Commission (%):</Text>
                                    <Text style={styles.value}>{data.platformCommission}</Text>
                                </View>
                            )}
                            {data.platformEarnings && (
                                <View style={styles.row}>
                                    <Text style={styles.label}>Platform Earnings:</Text>
                                    <Text style={styles.currency}>{data.platformEarnings}</Text>
                                </View>
                            )}
                        </View>
                    )}

                    {/* Session Notes - Only show if has data */}
                    {data.sessionNotes && (
                        <View style={styles.section}>
                            <Text style={styles.sectionTitle}>Session Notes</Text>
                            <Text style={styles.notes}>{data.sessionNotes}</Text>
                        </View>
                    )}

                    {/* Metadata - Only show if has data */}
                    {data.createdAt && (
                        <View style={styles.section}>
                            <Text style={styles.sectionTitle}>Metadata</Text>
                            <View style={styles.row}>
                                <Text style={styles.label}>Created At:</Text>
                                <Text style={styles.value}>{data.createdAt}</Text>
                            </View>
                        </View>
                    )}

                    <View style={styles.footer}>
                        <Text>This is an official session record. Generated by Admin Portal.</Text>
                    </View>
                </Page>
            </Document>
        );

        // Generate and download
        try {
            const blob = await pdf(MyDocument).toBlob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `session_notes_${row._id || "unknown"}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("PDF generation error:", error);
            alert("Error generating PDF. Please try again.");
        }
    };

    return (
        <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                background: "rgba(0,0,0,0.5)",
                zIndex: 9999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflowY: "auto",
            }}
        >
            <div
                style={{
                    background: "#fff",
                    borderRadius: 8,
                    width: "95%",
                    maxWidth: 600,
                    padding: 30,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                    position: "relative",
                    margin: "20px auto",
                }}
            >
                <h5 style={{ marginTop: 0, marginBottom: 20 }}>Session Details</h5>
                <button
                    style={{
                        position: "absolute",
                        top: 10,
                        right: 16,
                        background: "none",
                        border: "none",
                        fontSize: 24,
                        cursor: "pointer",
                        color: "#888",
                    }}
                    onClick={handleCloseNotes}
                    aria-label="Close modal"
                >
                    &times;
                </button>

                <div style={{ marginBottom: 16, lineHeight: "1.8" }}>
                    {modalData.booking_tracking_id && (
                        <>
                            <b>Booking ID:</b> {modalData.booking_tracking_id} <br />
                        </>
                    )}
                    {modalData.user?.name && (
                        <>
                            <b>User:</b> {modalData.user.name} <br />
                        </>
                    )}
                    {modalData.astrologer?.name && (
                        <>
                            <b>Astrologer:</b> {modalData.astrologer.name} <br />
                        </>
                    )}
                    {modalData.service_type && (
                        <>
                            <b>Service Type:</b> {modalData.service_type.replace(/_/g, " ")} <br />
                        </>
                    )}
                    {modalData.mode_of_consultation && (
                        <>
                            <b>Mode:</b> {modalData.mode_of_consultation} <br />
                        </>
                    )}
                    {modalData.duration && (
                        <>
                            <b>Duration:</b> {formatSecondstoHHMMSS(modalData.duration)} ({modalData.charged_duration_in_minutes} min charged) <br />
                        </>
                    )}
                    {modalData.base_price_per_minute !== null && modalData.base_price_per_minute !== undefined && (
                        <>
                            <b>Base Price/Min:</b> ₹{Number(modalData.base_price_per_minute).toFixed(2)} <br />
                        </>
                    )}
                    {modalData.astrologer_offer_percentage !== null && modalData.astrologer_offer_percentage !== undefined && (
                        <>
                            <b>Astrologer Discount:</b> {modalData.astrologer_offer_percentage}% <br />
                        </>
                    )}
                    {modalData.platform_offer_percentage !== null && modalData.platform_offer_percentage !== undefined && (
                        <>
                            <b>Platform Discount:</b> {modalData.platform_offer_percentage}% <br />
                        </>
                    )}
                    {modalData.final_amount !== null && modalData.final_amount !== undefined && (
                        <>
                            <b>Total Amount:</b> ₹{Number(modalData.final_amount).toFixed(2)} <br />
                        </>
                    )}
                    {modalData.astrologer_earning_amount !== null && modalData.astrologer_earning_amount !== undefined && (
                        <>
                            <b>Astrologer Earnings:</b> ₹{Number(modalData.astrologer_earning_amount).toFixed(2)} <br />
                        </>
                    )}
                    {modalData.platform_commission_percentage !== null && modalData.platform_commission_percentage !== undefined && (
                        <>
                            <b>Platform Commission (%):</b> {Number(modalData.platform_commission_percentage).toFixed(2)}% <br />
                        </>
                    )}
                    {modalData.platform_commission_amount !== null && modalData.platform_commission_amount !== undefined && (
                        <>
                            <b>Platform Earnings:</b> ₹{Number(modalData.platform_commission_amount).toFixed(2)} <br />
                        </>
                    )}
                    {modalData.session_notes && modalData.session_notes.trim() !== "" && (
                        <>
                            <b>Session Notes:</b>
                            <pre style={{ whiteSpace: "pre-wrap", margin: "8px 0", background: "#f5f5f5", padding: "10px", borderRadius: 4, fontSize: 12 }}>
                                {modalData.session_notes}
                            </pre>
                        </>
                    )}
                    {modalData.createdAt && (
                        <>
                            <b>Created At:</b> {formateDateWithMonth(modalData.createdAt)} <br />
                        </>
                    )}
                </div>

                <div style={{ display: "flex", gap: "10px" }}>
                    <button type="button" className="btn btn-primary signbtn" onClick={() => handleDownloadNotesPDF(modalData)}>
                        Download as PDF
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={handleCloseNotes}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SessionLogDetails;
